import { Head, router, useForm, usePage } from "@inertiajs/react"
import { Briefcase, Calendar, CheckCircle, MapPin, Users } from "lucide-react"
import { useState } from "react"

import ContentActionMenu from "@/components/content-action-menu"
import ShareButton from "@/components/share-button"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import VerifiedBadge from "@/components/verified-badge"
import AppLayout from "@/layouts/app-layout"
import { typedPatch } from "@/lib/typed-router"
import type { UpdateApplicationStatus } from "@/types/router-mutations"
import type RequestsCreateOpportunityApplication from "@/types/serializers/RequestsCreateOpportunityApplication"
import type { OpportunitiesShow } from "@/types/serializers"

const POSITIONS: Record<string, string> = {
  portero: "Portero",
  defensa_central: "Defensa Central",
  lateral_derecho: "Lateral Derecho",
  lateral_izquierdo: "Lateral Izquierdo",
  mediocampista_central: "Mediocampista Central",
  mediocampista_ofensivo: "Mediocampista Ofensivo",
  mediocampista_defensivo: "Mediocampista Defensivo",
  extremo_derecho: "Extremo Derecho",
  extremo_izquierdo: "Extremo Izquierdo",
  delantero_centro: "Delantero Centro",
  segundo_delantero: "Segundo Delantero",
}

const STATUS_LABELS: Record<string, string> = {
  pendiente: "Pendiente",
  vista: "Vista",
  contactado: "Contactado",
  rechazada: "Rechazada",
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("es-AR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })
}

export default function OpportunityShow({
  opportunity,
  has_applied,
  is_poster,
  applications = [],
}: OpportunitiesShow) {
  const page = usePage()
  const auth = page.props.auth as { user: { role: string } }
  const userRole = auth?.user?.role
  const [showApplyForm, setShowApplyForm] = useState(false)
  const applyForm = useForm<RequestsCreateOpportunityApplication>({
    opportunity_application: { message: "" },
  })

  function handleApply(e: React.FormEvent) {
    e.preventDefault()
    applyForm.post(`/oportunidades/${opportunity.id}/postulaciones`, {
      onSuccess: () => {
        setShowApplyForm(false)
        applyForm.reset()
      },
    })
  }

  function handleStatusChange(applicationId: number, status: string) {
    typedPatch<UpdateApplicationStatus>(
      `/oportunidades/${opportunity.id}/postulaciones/${applicationId}`,
      { opportunity_application: { status } },
    )
  }

  return (
    <AppLayout>
      <Head title={`${opportunity.title} | KrownFT`} />

      <div className="min-h-svh">
        <div className="mx-auto max-w-4xl px-4 py-6">
          {/* Header */}
          <div className="mb-6">
            <div className="mb-2 flex items-center gap-2">
              <Briefcase className="text-krown-crown h-5 w-5" />
              <button
                className="text-krown-100 font-medium hover:text-white"
                onClick={() =>
                  router.visit(`/clubes/${opportunity.club_profile.slug}`)
                }
              >
                {opportunity.club_profile.name}
              </button>
              <VerifiedBadge
                show={opportunity.club_profile.verified}
                size="sm"
              />
            </div>
            <div className="mb-3 flex items-center gap-3">
              <h1 className="text-2xl font-bold">{opportunity.title}</h1>
              <ShareButton
                url={`${window.location.origin}/oportunidades/${opportunity.id}`}
                title={`${opportunity.title} en KrownFT - ¡Postulate! ${window.location.origin}/oportunidades/${opportunity.id}`}
              />
              <ContentActionMenu
                reportableType="Opportunity"
                reportableId={opportunity.id}
              />
            </div>
            <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-sm">
              <span className="bg-krown-200/20 text-krown-100 rounded-full px-3 py-1 text-sm font-medium">
                {POSITIONS[opportunity.position_needed] ??
                  opportunity.position_needed}
              </span>
              {opportunity.country && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {[opportunity.region, opportunity.country]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              )}
              {(opportunity.min_age ?? opportunity.max_age) && (
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {opportunity.min_age && opportunity.max_age
                    ? `${opportunity.min_age}-${opportunity.max_age} años`
                    : opportunity.min_age
                      ? `Desde ${opportunity.min_age} años`
                      : `Hasta ${opportunity.max_age} años`}
                </span>
              )}
              {opportunity.expires_at && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Vence: {formatDate(opportunity.expires_at)}
                </span>
              )}
            </div>
          </div>

          {/* Description */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h2 className="mb-3 text-lg font-semibold">Descripción</h2>
              <p className="text-muted-foreground whitespace-pre-wrap">
                {opportunity.description}
              </p>
            </CardContent>
          </Card>

          {/* Details */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h2 className="mb-3 text-lg font-semibold">Detalles</h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {opportunity.division && (
                  <div>
                    <span className="text-muted-foreground font-medium">
                      División:
                    </span>
                    <span className="ml-2">{opportunity.division}</span>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground font-medium">
                    Postulaciones:
                  </span>
                  <span className="ml-2">{opportunity.applications_count}</span>
                </div>
                <div>
                  <span className="text-muted-foreground font-medium">
                    Publicada:
                  </span>
                  <span className="ml-2">
                    {formatDate(opportunity.created_at)}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground font-medium">
                    Publicado por:
                  </span>
                  <span className="ml-2">
                    {opportunity.posted_by.full_name}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Apply section for players */}
          {userRole === "player" && !is_poster && (
            <Card className="mb-6">
              <CardContent className="p-6">
                {has_applied ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">Ya te postulaste</span>
                  </div>
                ) : showApplyForm ? (
                  <form onSubmit={handleApply} className="space-y-4">
                    <h2 className="text-lg font-semibold">Postularme</h2>
                    <Textarea
                      value={applyForm.data.opportunity_application.message}
                      onChange={(e) =>
                        applyForm.setData("opportunity_application", {
                          ...applyForm.data.opportunity_application,
                          message: e.target.value,
                        })
                      }
                      placeholder="Mensaje opcional para el club..."
                      rows={4}
                    />
                    {Object.keys(applyForm.errors).length > 0 && (
                      <div className="rounded-md bg-red-500/10 p-3 text-sm text-red-500">
                        {Object.values(applyForm.errors).flat().join(". ")}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button type="submit" disabled={applyForm.processing}>
                        {applyForm.processing
                          ? "Enviando..."
                          : "Enviar postulación"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowApplyForm(false)
                          applyForm.reset()
                        }}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </form>
                ) : (
                  <Button onClick={() => setShowApplyForm(true)}>
                    Postularme
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Applications list for poster */}
          {is_poster && applications.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h2 className="mb-4 text-lg font-semibold">Postulaciones</h2>
                <div className="space-y-4">
                  {applications.map((app) => (
                    <div
                      key={app.id}
                      className="flex items-center justify-between rounded-lg border p-4"
                    >
                      <div>
                        <button
                          className="text-krown-100 font-medium hover:text-white"
                          onClick={() =>
                            router.visit(
                              `/jugadores/${app.player_profile.slug}`,
                            )
                          }
                        >
                          {app.player_profile.user.full_name}
                        </button>
                        <div className="text-muted-foreground mt-1 text-sm">
                          {POSITIONS[app.player_profile.position] ??
                            app.player_profile.position}
                          {app.player_profile.city &&
                            ` · ${app.player_profile.city}, ${app.player_profile.country}`}
                        </div>
                        {app.message && (
                          <p className="text-muted-foreground mt-2 text-sm">
                            {app.message}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Select
                          value={app.status}
                          onValueChange={(v) => handleStatusChange(app.id, v)}
                        >
                          <SelectTrigger className="w-36">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_LABELS).map(
                              ([value, label]) => (
                                <SelectItem key={value} value={value}>
                                  {label}
                                </SelectItem>
                              ),
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
